import { clsx } from 'clsx'
import { type FC, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  type OfferV11,
  type PrescriptionPrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'

import { Rating } from 'base-ui'

import {
  FEATURE_TOGGLE_VOLUME_BASED_PRICES,
  useFeatureToggleContext,
} from '../../../../../feature-toggles'
import { priceGetFormattedPrice } from '../../../../../price'
import { TosSuperscriptHowProductRatingCalculated } from '../../../../../tos-superscripts'
import { offerAccessorOfferHasVolumePricing } from '../../../../model/offerAccessor/offerAccessorOfferHasVolumePricing'
import { priceAccessorGetDisplayPrice } from '../../../../model/priceAccessor/priceAccessorGetDisplayPrice'
import { priceAccessorGetFormattedUnitPrice } from '../../../../model/priceAccessor/priceAccessorGetFormattedUnitPrice'
import { productAccessorHasBestOfferFlag } from '../../../../model/productAccessor/productAccessorHasBestOfferFlag'
import { productAccessorOfferHasSellerPromotion } from '../../../../model/productAccessor/productAccessorOfferHasSellerPromotion'
import { shippingAccessorGetFormattedShippingLeadTime } from '../../../../model/shippingAccessor/shippingAccessorGetFormattedShippingLeadTime'
import { shippingAccessorGetFormattedShippingTypeCost } from '../../../../model/shippingAccessor/shippingAccessorGetFormattedShippingTypeCost'
import { shippingAccessorGetShippingByPriority } from '../../../../model/shippingAccessor/shippingAccessorGetShippingByPriority'
import { useProductPageContext } from '../../../productPageContext'
import { ProductSellerPromotionInfoBanner } from '../../../ProductSellerPromotionInfoBanner'

import { ProductOfferGoToProductCta } from './ProductOfferGoToProductCta'
import { ProductOfferSellerLink } from './ProductOfferSellerLink'
import { ProductOfferVolumePricingLabel } from './ProductOfferVolumePricingLabel'

type ProductOfferCardProps = {
  offer?: OfferV11
  prescriptionType: PrescriptionPrescriptionTypeEnum
}

const getOfferLabels = ({
  isVolumePricingFeatureEnabled,
  offer,
}: {
  isVolumePricingFeatureEnabled: boolean
  offer: OfferV11
}): ReactNode | null => {
  const shouldShowSellerPromotionLabel =
    productAccessorOfferHasSellerPromotion(offer)
  const labels = []

  if (productAccessorHasBestOfferFlag(offer)) {
    labels.push(
      <button
        className="flex items-center rounded-full border-light-tertiary bg-light-tertiary px-2.5 py-1 text-xs"
        data-qa-id="offer-label-best-offer"
        type="button"
      >
        <FormattedMessage id="productOffer.cheapest" />
      </button>,
    )
  }

  if (
    offerAccessorOfferHasVolumePricing(offer) &&
    isVolumePricingFeatureEnabled
  ) {
    labels.push(
      <ProductOfferVolumePricingLabel
        className="border-dark-primary-min bg-light-brand-medium px-3.5 py-1"
        currentOffer={offer}
      />,
    )
  }

  if (shouldShowSellerPromotionLabel) {
    labels.push(
      <button
        className="flex items-center rounded-full bg-light-brand-medium px-2.5 py-1 text-xs"
        data-qa-id="product-label-seller-promotion"
        type="button"
      >
        <FormattedMessage id="seller.promotion.product.badge" />
      </button>,
    )
  }

  return labels.length > 0 ? (
    <div className="absolute -top-3.5 flex flex-row items-start gap-2">
      {labels}
    </div>
  ) : null
}

export const ProductOfferCard: FC<ProductOfferCardProps> = ({
  offer,
  prescriptionType,
}) => {
  const { product } = useProductPageContext()
  const featureToggles = useFeatureToggleContext()
  const intl = useIntl()

  if (!offer) {
    return null
  }

  const isVolumePricingFeatureEnabled = Boolean(
    featureToggles[FEATURE_TOGGLE_VOLUME_BASED_PRICES],
  )

  const currentDelivery = shippingAccessorGetShippingByPriority(offer)
  const currentPrice = priceAccessorGetDisplayPrice(offer, prescriptionType)

  const isFreeShipping =
    typeof currentDelivery?.shippingFreeAmount === 'number' &&
    typeof currentPrice?.price === 'number' &&
    currentPrice.price >= currentDelivery.shippingFreeAmount

  const shippingPriceByType = currentDelivery
    ? shippingAccessorGetFormattedShippingTypeCost({
        intl,
        isFreeShipping,
        shippingTypeCode: currentDelivery.shippingTypeCode,
        totalShippingPrice: currentDelivery.totalShippingPrice,
      })
    : ''

  const labelsBlock = getOfferLabels({ isVolumePricingFeatureEnabled, offer })

  return (
    <div
      className={clsx(
        'relative rounded-2xl bg-light-primary-low px-4 py-6',
        labelsBlock && 'mt-4',
      )}
      data-qa-id={`product-offer-card-${offer.offerId}`}
    >
      {labelsBlock}
      <div className="grid grid-cols-3 justify-between gap-6 tablet:grid-cols-10">
        <div className="col-span-1 flex flex-col justify-center tablet:col-span-2">
          {currentPrice?.price ? (
            <span className="text-xl font-medium">
              {priceGetFormattedPrice(currentPrice.price, intl)}
            </span>
          ) : null}
          <span className="text-dark-primary-low">
            {priceAccessorGetFormattedUnitPrice(offer, intl)}
          </span>
        </div>
        <div className="col-span-2 flex flex-col justify-center tablet:col-span-3">
          <span className="font-medium text-dark-success-medium">
            {shippingAccessorGetFormattedShippingLeadTime(
              intl,
              currentDelivery?.deliveryTime,
            )}
          </span>
          <span className="text-xs text-dark-primary-medium">
            {shippingPriceByType}
          </span>
          {currentDelivery?.shippingFreeAmount && !isFreeShipping ? (
            <span className="text-xs text-dark-primary-medium">
              <FormattedMessage
                id="buyBox.link.label.belowFreeShippingLimit"
                values={{
                  amount: priceGetFormattedPrice(
                    currentDelivery.shippingFreeAmount,
                    intl,
                  ),
                }}
              />
            </span>
          ) : null}
        </div>
        <div className="col-span-2 flex flex-col justify-center gap-1 tablet:col-span-3">
          <span className="w-3/4 text-xs text-dark-primary-low">
            <FormattedMessage id="productOffer.soldBy" />
          </span>
          {offer.seller ? (
            <ProductOfferSellerLink seller={offer.seller} />
          ) : null}
          {offer.seller?.sellerGrade && offer.seller.sellerRatingCount ? (
            <span className="flex text-xs text-dark-primary-medium">
              <Rating
                className="text-l"
                readOnly
                value={offer.seller.sellerGrade}
              />
              <TosSuperscriptHowProductRatingCalculated className="top-1" />
              {`(${offer.seller.sellerRatingCount})`}
            </span>
          ) : null}
        </div>
        <div className="col-span-1 flex items-end justify-end tablet:col-span-2 tablet:items-center">
          <ProductOfferGoToProductCta offer={offer} />
        </div>
      </div>
      <ProductSellerPromotionInfoBanner
        className="mt-6 items-center"
        hideSellerSerpLink
        offer={offer}
        product={product}
        size="xs"
      />
    </div>
  )
}
